import { useEffect, useRef, useState } from "react";

import { ClientsService } from "../../clients/services/clientsService";
import type { ClientListItem } from "../../clients/types/clients-api.types";
import { normalizeClientListItem } from "../../clients/utils/normalizeClient";

const DEFAULT_DEBOUNCE_MS = 350;
const LIST_LIMIT = 50;

type Result = {
  clients: ClientListItem[];
  isLoading: boolean;
  debouncedSearch: string;
};

export function useDebouncedAuditClientSearch(
  enabled: boolean,
  searchInput: string,
  debounceMs: number = DEFAULT_DEBOUNCE_MS,
): Result {
  const [debouncedInput, setDebouncedInput] = useState(searchInput);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedInput(searchInput), debounceMs);
    return () => window.clearTimeout(t);
  }, [searchInput, debounceMs]);

  useEffect(() => {
    if (!enabled) {
      // Benign reset when the search is disabled — synchronizes with external `enabled` flag, no cascade.
      // oxlint-disable-next-line react-hooks-eslint/set-state-in-effect
      setClients([]);
      setIsLoading(false);
      return;
    }

    const trimmed = debouncedInput.trim();
    if (!trimmed) {
      setClients([]);
      setIsLoading(false);
      return;
    }

    const id = ++requestIdRef.current;
    setIsLoading(true);

    ClientsService.fetchList({
      skip: 0,
      limit: LIST_LIMIT,
      search: trimmed,
    })
      .then((res) => {
        if (id !== requestIdRef.current) return;
        setClients((res.data.data ?? []).map(normalizeClientListItem));
      })
      .catch(() => {
        if (id !== requestIdRef.current) return;
        setClients([]);
      })
      .finally(() => {
        if (id === requestIdRef.current) setIsLoading(false);
      });
  }, [enabled, debouncedInput]);

  return { clients, isLoading, debouncedSearch: debouncedInput };
}
